import { DataGridPremiumProps } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectSchema } from 'yup';

import {
  DataGridCRUDConfig,
  useDataGridCRUDValidation,
  useDataGridRowFileChangesState,
} from '~/components/SolaForceDataGrid/hooks';
import {
  deleteEmployeeDocument,
  saveEmployeeDocument,
  useGetCompanyDocuments,
  useGetEmployeeDocuments,
} from '~/data/documents';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  DocumentsEntry,
  DocumentsEntryRow,
  convertDocumentTypeToDocumentsEntry,
  convertDocumentsEntryToSubmission,
  documentSubmissionToFormData,
} from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/data';
import { UseDocumentsTableColumnsProps } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/useDocumentsTableColumns';
import { legacyTranslationInterpolation } from '~/utils/helper';

export type UseDocumentsOutput = {
  isLoading: boolean;
  dataGridProps: Omit<DataGridPremiumProps<DocumentsEntry>, 'columns'>;
  validationSchema: ObjectSchema;
  canPrintDocument: boolean;
  columnsConfig: UseDocumentsTableColumnsProps;
  crudConfig: DataGridCRUDConfig<DocumentsEntry, number>;
};

export const useDocumentsTable = (): UseDocumentsOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    selectedEmployeeId,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const {
    isLoading: isLoadingWorkContractDocuments,
    mutate: mutatePrintWorkContracts,
  } = useGetCompanyDocuments();

  const { canPrintDocument, canApproveDocument, canEditDocumentsTable } =
    useMemo(() => {
      const canPrintDocument = hasRoleAccessOverEmployee([
        Roles.HR_ADMIN,
        Roles.MANAGER,
        Roles.ALT_MANAGER,
      ]);

      // TODO Add Edit & Approval access check
      return {
        canEditDocumentsTable: true,
        canApproveDocument: true,
        canPrintDocument,
      };
    }, [hasRoleAccessOverEmployee]);

  const { getEnums } = useAllEnums();
  const { documentTypes, documentApprovalStatuses } = useMemo(() => {
    return {
      documentTypes: getEnums(EnumListKey.DOCUMENT_TYPE),
      documentApprovalStatuses: getEnums(EnumListKey.DOCUMENT_APPROVAL_STATUS),
    };
  }, [getEnums]);

  // Document Approval
  const onDocumentApproval = useCallback(
    (row: DocumentsEntry) => {
      console.log(row, 'onApproveDocument');
      enqueueSnackbar('Not yet implemented', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  // Table data
  const {
    data: documentData,
    isLoading: isLoadingDocumentsData,
    mutate: mutateDocumentsData,
  } = useGetEmployeeDocuments({ employeeId: selectedEmployeeId });

  const { documents } = useMemo(() => {
    return {
      documents: documentData.map(convertDocumentTypeToDocumentsEntry),
    };
  }, [documentData]);

  // Temporary storage for updated files
  const { fileChanges, updateFileChanges, resetFileChange } =
    useDataGridRowFileChangesState();

  const validationSchema = yup.object().shape<DocumentsEntryRow>({
    id: yup.number(),
    type: yup
      .string()
      .required(t('laFillManFields'))
      .oneOf(
        documentTypes.map((type) => type.code),
        t('laFillManFields'),
      ),
    name: yup.string().required(t('laFillManFields')),
    description: yup
      .string()
      .defined()
      .max(
        160,
        t('text_validate_max_length', {
          max: 160,
          interpolation: legacyTranslationInterpolation,
        }),
      ),
    expires: yup.date(),
    approval: yup.string(),
    approve: yup.string(),
  });

  const includeFileChangesToSubmission = useCallback(
    (submitRow: DocumentsEntry): DocumentsEntry => {
      const fileChange = fileChanges.find((fc) => fc.id === submitRow.id);
      if (!!fileChange) {
        const { file } = fileChange;

        return {
          ...submitRow,
          attachment: file,
          name: file?.name ?? '',
          fileSize: file?.size,
          fileHash: undefined,
        };
      }

      return submitRow;
    },
    [fileChanges],
  );

  const handleSubmitPromise = useCallback(
    async <T>(promise: Promise<T>) => {
      return promise
        .then((result) => {
          mutateDocumentsData();
          mutatePrintWorkContracts();
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
          return Promise.resolve(result);
        })
        .catch((error) => {
          enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
          return Promise.reject(error);
        });
    },
    [t, enqueueSnackbar, mutateDocumentsData, mutatePrintWorkContracts],
  );

  // Handle Add New & Update
  const handleSubmit = useCallback(
    async (
      submitRow: DocumentsEntry,
      method: 'POST' | 'UPDATE',
    ): Promise<DocumentsEntry> => {
      const submitData = includeFileChangesToSubmission(submitRow);

      return validationSchema
        .validate(submitData, { abortEarly: false })
        .then(async () => {
          const isUpdate = method === 'UPDATE';
          const submission = convertDocumentsEntryToSubmission(
            submitData,
            isUpdate,
          );

          return handleSubmitPromise(
            saveEmployeeDocument(
              selectedEmployeeId,
              documentSubmissionToFormData(submission),
              isUpdate ? submission.fDocumentId : undefined,
            ),
          ).then((result) => {
            resetFileChange(submitRow.id);
            return Promise.resolve(convertDocumentTypeToDocumentsEntry(result));
          });
        })
        .catch((err) => {
          return Promise.reject({ rowId: submitRow.id, error: err });
        });
    },
    [
      validationSchema,
      includeFileChangesToSubmission,
      handleSubmitPromise,
      selectedEmployeeId,
      resetFileChange,
    ],
  );

  // Handle Delete
  const onSubmitDelete = useCallback(
    async (deletedRow: DocumentsEntry) => {
      return handleSubmitPromise(
        deleteEmployeeDocument(selectedEmployeeId, deletedRow.id),
      ).then(() => {
        resetFileChange(deletedRow.id);
      });
    },
    [handleSubmitPromise, selectedEmployeeId, resetFileChange],
  );

  const { actionsValidation, columnsValidation, dataGridProps } =
    useDataGridCRUDValidation<DocumentsEntry>({
      validationSchema,
    });

  const crudConfig: DataGridCRUDConfig<DocumentsEntry, number> = {
    data: {
      onSubmitCreate: (createdRow: DocumentsEntry) =>
        handleSubmit(createdRow, 'POST'),
      onSubmitUpdate: (createdRow: DocumentsEntry) =>
        handleSubmit(createdRow, 'UPDATE'),
      onSubmitDelete,
      onCancelEdit: (editedRow) => {
        resetFileChange(editedRow.id);
      },
    },
    getRowId: (row) => row.id,
    addNewRow: {
      getNewId: (rows) => rows.length,
      getInitialRowData: (newId: DocumentsEntry['id']): DocumentsEntry => {
        return {
          id: newId,
          type: '',
          name: '',
          description: '',
          approval: '',
          attachment: undefined,
          fileSize: undefined,
        };
      },
      fieldToFocus: 'type',
    },
    isRowEditable: (params) => {
      return !params.row.readOnly;
    },
    getDeleteRowMessage: (row) => {
      return `${t('delete')}: ${row.name}`;
    },
    actionsValidation,
    permissions: {
      canAddNew: canEditDocumentsTable,
    },
  };

  return {
    isLoading: isLoadingDocumentsData || isLoadingWorkContractDocuments,
    validationSchema,
    columnsConfig: {
      selectedEmployeeId,
      canEdit: canEditDocumentsTable,
      documentTypes,
      documentApprovalStatuses,
      documentApproval: {
        onDocumentApproval,
        enabled: canApproveDocument,
      },
      columnsValidation,
      updateFileChanges,
      fileChanges,
    },
    canPrintDocument,
    crudConfig,
    dataGridProps: {
      ...dataGridProps,
      rows: documents,
    },
  };
};
